import React, { useState, useEffect, useRef } from 'react';
import { Search, MessageCircle, User, Bot, ThumbsUp, ThumbsDown, Send, Sparkles, CheckCircle, Calendar, ExternalLink } from 'lucide-react';
import FAQChatInterface from './FAQChatInterface';

const FAQSection = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showTyping, setShowTyping] = useState(false);
  const [feedback, setFeedback] = useState({});
  const answerRef = useRef(null);

  const faqCategories = {
    'general': {
      title: 'General Consulting',
      icon: '💼',
      color: 'from-blue-500 to-cyan-500',
      faqs: [
        {
          question: "What industries do you specialize in?",
          answer: "We have extensive experience across Banking & Financial Services, Manufacturing, Technology, Healthcare, and Automotive industries. Our methodologies are adaptable to any sector requiring operational excellence, compliance, and process optimization."
        },
        {
          question: "How do you ensure confidentiality and data security?",
          answer: "We maintain strict confidentiality agreements and follow ISO 27001 information security standards. All client data is protected through encrypted communications, secure document sharing platforms, and comprehensive non-disclosure protocols."
        },
        {
          question: "What makes your approach different from other consultants?",
          answer: "Our unique combination of 21+ years experience, certified process excellence expertise, AI/RPA implementation experience, and multi-industry operational transformation background. We deliver measurable results with a proven track record of 100% success rate."
        }
      ]
    },
    'process': {
      title: 'Process Excellence',
      icon: '⚙️',
      color: 'from-purple-500 to-pink-500',
      faqs: [
        {
          question: "What is the typical timeline for Six Sigma projects?",
          answer: "Six Sigma projects typically range from 3-6 months depending on scope and complexity. Our DMAIC methodology ensures structured progress with clear milestones: Define (2 weeks), Measure (3-4 weeks), Analyze (4-6 weeks), Improve (6-8 weeks), Control (2-3 weeks)."
        },
        {
          question: "What kind of results can we expect from Lean implementation?",
          answer: "Based on our track record, clients typically see 25-40% cycle time reduction, 15-30% waste elimination, and 20-35% efficiency improvements. Specific results depend on current state maturity and implementation scope."
        },
        {
          question: "Do you provide training for our internal team?",
          answer: "Yes, we offer comprehensive training programs including Green Belt certification, internal auditor training, and customized workshops. We've trained 500+ professionals with a 95% certification success rate."
        }
      ]
    },
    'compliance': {
      title: 'Compliance & Risk',
      icon: '🛡️',
      color: 'from-green-500 to-emerald-500',
      faqs: [
        {
          question: "Which ISO standards do you specialize in?",
          answer: "We specialize in ISO 9001:2015 (Quality Management), ISO 27001:2022 (Information Security), and ISO 42001 (AI Management Systems). Our Lead Auditor credentials ensure comprehensive implementation and audit readiness."
        },
        {
          question: "How long does ISO certification typically take?",
          answer: "ISO certification projects typically take 6-12 months depending on organizational readiness and standard complexity. We've achieved 100% first-time certification success with zero major non-conformities in recent audits."
        },
        {
          question: "What is your approach to risk management?",
          answer: "We follow a comprehensive risk assessment methodology covering operational, financial, compliance, and strategic risks. Our approach includes risk identification, impact analysis, mitigation strategies, and ongoing monitoring frameworks."
        }
      ]
    },
    'technology': {
      title: 'AI & Automation',
      icon: '🤖',
      color: 'from-orange-500 to-red-500',
      faqs: [
        {
          question: "What types of processes are suitable for RPA?",
          answer: "RPA works best with rule-based, repetitive processes involving structured data. Examples include invoice processing, data entry, report generation, and compliance monitoring. We've successfully automated 60% of manual tasks in typical implementations."
        },
        {
          question: "How do you ensure AI systems are compliant and ethical?",
          answer: "We implement AI governance frameworks based on ISO 42001 standards, ensuring transparency, accountability, and bias mitigation. Our approach includes risk assessment, ethical AI principles, and continuous monitoring for compliance."
        },
        {
          question: "What is the ROI timeline for automation projects?",
          answer: "Most automation projects achieve ROI within 6-12 months. Typical benefits include 40-90% reduction in manual effort, 99%+ accuracy rates, and 24/7 processing capability. Our recent projects have delivered 300-400% ROI within first year."
        }
      ]
    },
    'pricing': {
      title: 'Investment & Pricing',
      icon: '💰',
      color: 'from-yellow-500 to-amber-500',
      faqs: [
        {
          question: "How do you structure your pricing?",
          answer: "We offer flexible engagement models: project-based fixed pricing, hourly consulting rates, or success-based fees tied to measurable outcomes. Most clients prefer project-based pricing for predictable budgeting and clear deliverables."
        },
        {
          question: "What is the typical investment range for projects?",
          answer: "Refer Pricing Page"
        },
        {
          question: "Do you offer payment plans or phased payments?",
          answer: "Yes, we structure payments based on project milestones and deliverables. Typical structure: 30% upfront, 40% at mid-project milestone, and 30% upon completion. This ensures alignment with project progress and client cash flow."
        }
      ]
    }
  };

  const toggleFaq = (categoryKey, faqIndex) => {
    const key = `${categoryKey}-${faqIndex}`;
    if (openFaq === key) {
      setOpenFaq(null);
      setShowTyping(false);
    } else {
      setOpenFaq(key);
      setShowTyping(true);
      // Simulate typing delay
      setTimeout(() => {
        setShowTyping(false);
        // Scroll to answer
        setTimeout(() => {
          answerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      }, 800);
    }
  };

  const handleFeedback = (faqKey, isHelpful) => {
    setFeedback({ ...feedback, [faqKey]: isHelpful });
  };

  const filteredCategories = () => {
    if (selectedCategory === 'all') {
      return Object.entries(faqCategories);
    }
    return [[selectedCategory, faqCategories[selectedCategory]]];
  };

  const searchFilteredFaqs = (faqs) => {
    if (!searchQuery.trim()) return faqs;
    return faqs.filter(faq => 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    );
  };

  // Simplified FAQ Section with AI Chat
  return (
    <section className="relative py-20 overflow-hidden" id="faq" style={{ background: 'linear-gradient(to bottom, #0f172a 0%, #1e293b 100%)' }}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-turquoise-500/10 border border-turquoise-500/30 rounded-full px-4 py-2 mb-4">
            <Sparkles size={16} className="text-turquoise-400" />
            <span className="text-sm text-turquoise-400 font-medium">AI-Powered FAQ Assistant</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            Ask <span style={{ color: '#14b8a6' }}>Me</span> <span style={{ color: '#14b8a6' }}>Anything</span>
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Get instant AI-powered answers about our consulting services, pricing, and more!
          </p>
        </div>

        {/* AI Chat Interface */}
        <FAQChatInterface />

        {/* Legacy FAQ code temporarily hidden */}
        <div style={{ display: 'none' }}>
          <div className="max-w-2xl mx-auto mb-8">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search for answers... (e.g., 'pricing', 'timeline', 'Six Sigma')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-800/50 border border-gray-700 rounded-xl pl-12 pr-4 py-4 text-white placeholder-gray-500 focus:outline-none focus:border-turquoise-500 transition-colors"
            />
          </div>
        </div>

        {/* Search shows filtered results */}
        {searchQuery ? (
          <div className="max-w-4xl mx-auto">
            <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl p-6 md:p-8 space-y-6">
              {filteredCategories().map(([categoryKey, category]) => {
                const filteredFaqs = searchFilteredFaqs(category.faqs);
                
                if (filteredFaqs.length === 0) return null;

                return (
                  <div key={categoryKey} className="space-y-6">
                    {/* Category Header */}
                    <div className="flex items-center gap-3 pb-4 border-b border-gray-800">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-r ${category.color} flex items-center justify-center text-xl`}>
                        {category.icon}
                      </div>
                      <h3 className="text-xl font-semibold" style={{ color: '#14b8a6' }}>{category.title}</h3>
                    </div>

                    {/* FAQ Items as Cards in Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredFaqs.map((faq, index) => {
                      const isOpen = openFaq === `${categoryKey}-${index}`;
                      const faqKey = `${categoryKey}-${index}`;
                      const hasFeedback = feedback[faqKey] !== undefined;

                      return (
                        <div 
                          key={index} 
                          className={`bg-gray-800/50 border rounded-xl transition-all duration-300 overflow-hidden ${
                            isOpen 
                              ? 'border-turquoise-500 shadow-lg shadow-turquoise-500/20' 
                              : 'border-gray-700 hover:border-gray-600'
                          }`}
                        >
                          {/* Question Card */}
                          <button
                            onClick={() => toggleFaq(categoryKey, index)}
                            className="w-full p-6 text-left hover:bg-gray-800/70 transition-colors"
                          >
                            <div className="flex items-start gap-3">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-r from-turquoise-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                                <User size={20} className="text-white" />
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-white text-lg leading-relaxed">{faq.question}</p>
                              </div>
                              <div className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="text-turquoise-400">
                                  <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                              </div>
                            </div>
                          </button>

                          {/* Answer Section */}
                          {isOpen && (
                            <div className="px-6 pb-6 animate-fadeIn">
                              <div className="flex items-start gap-3 mb-4">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                                  <Bot size={20} className="text-white" />
                                </div>
                                <div className="flex-1 pt-2">
                                  {/* Typing Indicator */}
                                  {showTyping ? (
                                    <div className="flex gap-1">
                                      <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                      <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                      <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                    </div>
                                  ) : (
                                    <div ref={answerRef}>
                                      <p className="text-gray-300 leading-relaxed mb-4">{faq.answer}</p>
                                      
                                      {/* Quick Actions */}
                                      <div className="flex flex-wrap gap-2 mb-4">
                                        <button 
                                          onClick={() => window.location.href = '/book-consultation'}
                                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-turquoise-500/10 hover:bg-turquoise-500/20 border border-turquoise-500/30 rounded-lg text-sm text-turquoise-400 transition-colors"
                                        >
                                          <Calendar size={14} />
                                          Book Consultation
                                        </button>
                                        <button 
                                          onClick={() => window.location.href = '/pricing'}
                                          className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 hover:bg-gray-700 border border-gray-600 rounded-lg text-sm text-gray-300 transition-colors"
                                        >
                                          <ExternalLink size={14} />
                                          View Pricing
                                        </button>
                                      </div>

                                      {/* Feedback Buttons */}
                                      <div className="flex items-center gap-4 pt-4 border-t border-gray-700">
                                        <span className="text-sm text-gray-400">Helpful?</span>
                                        <div className="flex gap-2">
                                          <button
                                            onClick={() => handleFeedback(faqKey, true)}
                                            className={`p-2 rounded-lg transition-all ${
                                              feedback[faqKey] === true
                                                ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                                : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 border border-gray-600'
                                            }`}
                                          >
                                            <ThumbsUp size={16} />
                                          </button>
                                          <button
                                            onClick={() => handleFeedback(faqKey, false)}
                                            className={`p-2 rounded-lg transition-all ${
                                              feedback[faqKey] === false
                                                ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                                                : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 border border-gray-600'
                                            }`}
                                          >
                                            <ThumbsDown size={16} />
                                          </button>
                                        </div>
                                        {hasFeedback && (
                                          <span className="text-sm text-turquoise-400">
                                            Thanks!
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}

              {/* No Results */}
              {filteredCategories().every(([_, category]) => searchFilteredFaqs(category.faqs).length === 0) && (
                <div className="text-center py-12">
                  <MessageCircle size={48} className="text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">No FAQs found matching "{searchQuery}"</p>
                  <button
                    onClick={() => setSearchQuery('')}
                    className="text-turquoise-400 hover:text-turquoise-300 underline"
                  >
                    Clear search
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Category Cards Grid Layout */
          <div className="max-w-5xl mx-auto mb-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Row 1: General Consulting and Process Excellence */}
              {['general', 'process'].map((categoryKey) => {
                const category = faqCategories[categoryKey];
                const isExpanded = selectedCategory === categoryKey;
                
                return (
                  <div key={categoryKey} className="space-y-4">
                    {/* Category Card */}
                    <button
                      onClick={() => setSelectedCategory(isExpanded ? 'all' : categoryKey)}
                      className={`w-full p-8 rounded-2xl border-2 transition-all duration-300 text-left ${
                        isExpanded
                          ? 'border-turquoise-500 bg-gradient-to-br from-turquoise-500/10 to-cyan-500/10 shadow-lg shadow-turquoise-500/20'
                          : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800/70'
                      }`}
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${category.color} flex items-center justify-center text-3xl flex-shrink-0`}>
                          {category.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold mb-2" style={{ color: '#14b8a6' }}>
                            {category.title}
                          </h3>
                          <p className="text-gray-400 text-sm">
                            {category.faqs.length} questions available
                          </p>
                        </div>
                        <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-turquoise-400">
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {category.faqs.slice(0, 2).map((faq, idx) => (
                          <span key={idx} className="text-xs text-gray-500 bg-gray-700/50 px-3 py-1 rounded-full">
                            {faq.question.substring(0, 30)}...
                          </span>
                        ))}
                      </div>
                    </button>

                    {/* Expanded Questions */}
                    {isExpanded && (
                      <div className="space-y-3 animate-fadeIn">
                        {category.faqs.map((faq, index) => {
                          const isOpen = openFaq === `${categoryKey}-${index}`;
                          const faqKey = `${categoryKey}-${index}`;
                          const hasFeedback = feedback[faqKey] !== undefined;

                          return (
                            <div 
                              key={index} 
                              className={`bg-gray-800/50 border rounded-xl transition-all duration-300 overflow-hidden ${
                                isOpen 
                                  ? 'border-turquoise-500 shadow-lg shadow-turquoise-500/20' 
                                  : 'border-gray-700 hover:border-gray-600'
                              }`}
                            >
                              {/* Question Card */}
                              <button
                                onClick={() => toggleFaq(categoryKey, index)}
                                className="w-full p-4 text-left hover:bg-gray-800/70 transition-colors"
                              >
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-turquoise-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                                    <User size={16} className="text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-semibold text-white leading-relaxed">{faq.question}</p>
                                  </div>
                                  <div className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-turquoise-400">
                                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </div>
                                </div>
                              </button>

                              {/* Answer Section */}
                              {isOpen && (
                                <div className="px-4 pb-4 animate-fadeIn">
                                  <div className="flex items-start gap-3 mb-3">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                                      <Bot size={16} className="text-white" />
                                    </div>
                                    <div className="flex-1 pt-1">
                                      {showTyping ? (
                                        <div className="flex gap-1">
                                          <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                          <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                          <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                        </div>
                                      ) : (
                                        <div ref={answerRef}>
                                          <p className="text-gray-300 text-sm leading-relaxed mb-3">{faq.answer}</p>
                                          
                                          <div className="flex flex-wrap gap-2 mb-3">
                                            <button 
                                              onClick={() => window.location.href = '/book-consultation'}
                                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-turquoise-500/10 hover:bg-turquoise-500/20 border border-turquoise-500/30 rounded-lg text-xs text-turquoise-400 transition-colors"
                                            >
                                              <Calendar size={12} />
                                              Book Consultation
                                            </button>
                                            <button 
                                              onClick={() => window.location.href = '/pricing'}
                                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 hover:bg-gray-700 border border-gray-600 rounded-lg text-xs text-gray-300 transition-colors"
                                            >
                                              <ExternalLink size={12} />
                                              View Pricing
                                            </button>
                                          </div>

                                          <div className="flex items-center gap-3 pt-3 border-t border-gray-700">
                                            <span className="text-xs text-gray-400">Helpful?</span>
                                            <div className="flex gap-2">
                                              <button
                                                onClick={() => handleFeedback(faqKey, true)}
                                                className={`p-1.5 rounded-lg transition-all ${
                                                  feedback[faqKey] === true
                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                                    : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 border border-gray-600'
                                                }`}
                                              >
                                                <ThumbsUp size={14} />
                                              </button>
                                              <button
                                                onClick={() => handleFeedback(faqKey, false)}
                                                className={`p-1.5 rounded-lg transition-all ${
                                                  feedback[faqKey] === false
                                                    ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                                                    : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 border border-gray-600'
                                                }`}
                                              >
                                                <ThumbsDown size={14} />
                                              </button>
                                            </div>
                                            {hasFeedback && (
                                              <span className="text-xs text-turquoise-400">Thanks!</span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Row 2: Compliance & Risk and AI & Automation */}
              {['compliance', 'technology'].map((categoryKey) => {
                const category = faqCategories[categoryKey];
                const isExpanded = selectedCategory === categoryKey;
                
                return (
                  <div key={categoryKey} className="space-y-4">
                    {/* Category Card */}
                    <button
                      onClick={() => setSelectedCategory(isExpanded ? 'all' : categoryKey)}
                      className={`w-full p-8 rounded-2xl border-2 transition-all duration-300 text-left ${
                        isExpanded
                          ? 'border-turquoise-500 bg-gradient-to-br from-turquoise-500/10 to-cyan-500/10 shadow-lg shadow-turquoise-500/20'
                          : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800/70'
                      }`}
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${category.color} flex items-center justify-center text-3xl flex-shrink-0`}>
                          {category.icon}
                        </div>
                        <div className="flex-1">
                          <h3 className="text-2xl font-bold mb-2" style={{ color: '#14b8a6' }}>
                            {category.title}
                          </h3>
                          <p className="text-gray-400 text-sm">
                            {category.faqs.length} questions available
                          </p>
                        </div>
                        <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-turquoise-400">
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {category.faqs.slice(0, 2).map((faq, idx) => (
                          <span key={idx} className="text-xs text-gray-500 bg-gray-700/50 px-3 py-1 rounded-full">
                            {faq.question.substring(0, 30)}...
                          </span>
                        ))}
                      </div>
                    </button>

                    {/* Expanded Questions */}
                    {isExpanded && (
                      <div className="space-y-3 animate-fadeIn">
                        {category.faqs.map((faq, index) => {
                          const isOpen = openFaq === `${categoryKey}-${index}`;
                          const faqKey = `${categoryKey}-${index}`;
                          const hasFeedback = feedback[faqKey] !== undefined;

                          return (
                            <div 
                              key={index} 
                              className={`bg-gray-800/50 border rounded-xl transition-all duration-300 overflow-hidden ${
                                isOpen 
                                  ? 'border-turquoise-500 shadow-lg shadow-turquoise-500/20' 
                                  : 'border-gray-700 hover:border-gray-600'
                              }`}
                            >
                              <button
                                onClick={() => toggleFaq(categoryKey, index)}
                                className="w-full p-4 text-left hover:bg-gray-800/70 transition-colors"
                              >
                                <div className="flex items-start gap-3">
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-turquoise-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                                    <User size={16} className="text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <p className="font-semibold text-white leading-relaxed">{faq.question}</p>
                                  </div>
                                  <div className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-turquoise-400">
                                      <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                  </div>
                                </div>
                              </button>

                              {isOpen && (
                                <div className="px-4 pb-4 animate-fadeIn">
                                  <div className="flex items-start gap-3 mb-3">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                                      <Bot size={16} className="text-white" />
                                    </div>
                                    <div className="flex-1 pt-1">
                                      {showTyping ? (
                                        <div className="flex gap-1">
                                          <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                          <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                          <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                        </div>
                                      ) : (
                                        <div ref={answerRef}>
                                          <p className="text-gray-300 text-sm leading-relaxed mb-3">{faq.answer}</p>
                                          
                                          <div className="flex flex-wrap gap-2 mb-3">
                                            <button 
                                              onClick={() => window.location.href = '/book-consultation'}
                                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-turquoise-500/10 hover:bg-turquoise-500/20 border border-turquoise-500/30 rounded-lg text-xs text-turquoise-400 transition-colors"
                                            >
                                              <Calendar size={12} />
                                              Book Consultation
                                            </button>
                                            <button 
                                              onClick={() => window.location.href = '/pricing'}
                                              className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 hover:bg-gray-700 border border-gray-600 rounded-lg text-xs text-gray-300 transition-colors"
                                            >
                                              <ExternalLink size={12} />
                                              View Pricing
                                            </button>
                                          </div>

                                          <div className="flex items-center gap-3 pt-3 border-t border-gray-700">
                                            <span className="text-xs text-gray-400">Helpful?</span>
                                            <div className="flex gap-2">
                                              <button
                                                onClick={() => handleFeedback(faqKey, true)}
                                                className={`p-1.5 rounded-lg transition-all ${
                                                  feedback[faqKey] === true
                                                    ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                                    : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 border border-gray-600'
                                                }`}
                                              >
                                                <ThumbsUp size={14} />
                                              </button>
                                              <button
                                                onClick={() => handleFeedback(faqKey, false)}
                                                className={`p-1.5 rounded-lg transition-all ${
                                                  feedback[faqKey] === false
                                                    ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                                                    : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 border border-gray-600'
                                                }`}
                                              >
                                                <ThumbsDown size={14} />
                                              </button>
                                            </div>
                                            {hasFeedback && (
                                              <span className="text-xs text-turquoise-400">Thanks!</span>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Row 3: Investment & Pricing (Full Width) */}
              <div className="md:col-span-2">
                {(() => {
                  const categoryKey = 'pricing';
                  const category = faqCategories[categoryKey];
                  const isExpanded = selectedCategory === categoryKey;
                  
                  return (
                    <div className="space-y-4">
                      {/* Category Card */}
                      <button
                        onClick={() => setSelectedCategory(isExpanded ? 'all' : categoryKey)}
                        className={`w-full p-8 rounded-2xl border-2 transition-all duration-300 text-left ${
                          isExpanded
                            ? 'border-turquoise-500 bg-gradient-to-br from-turquoise-500/10 to-cyan-500/10 shadow-lg shadow-turquoise-500/20'
                            : 'border-gray-700 bg-gray-800/50 hover:border-gray-600 hover:bg-gray-800/70'
                        }`}
                      >
                        <div className="flex items-start gap-4 mb-4">
                          <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${category.color} flex items-center justify-center text-3xl flex-shrink-0`}>
                            {category.icon}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-2xl font-bold mb-2" style={{ color: '#14b8a6' }}>
                              {category.title}
                            </h3>
                            <p className="text-gray-400 text-sm">
                              {category.faqs.length} questions available
                            </p>
                          </div>
                          <div className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" className="text-turquoise-400">
                              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {category.faqs.slice(0, 2).map((faq, idx) => (
                            <span key={idx} className="text-xs text-gray-500 bg-gray-700/50 px-3 py-1 rounded-full">
                              {faq.question.substring(0, 30)}...
                            </span>
                          ))}
                        </div>
                      </button>

                      {/* Expanded Questions in 2 columns */}
                      {isExpanded && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 animate-fadeIn">
                          {category.faqs.map((faq, index) => {
                            const isOpen = openFaq === `${categoryKey}-${index}`;
                            const faqKey = `${categoryKey}-${index}`;
                            const hasFeedback = feedback[faqKey] !== undefined;

                            return (
                              <div 
                                key={index} 
                                className={`bg-gray-800/50 border rounded-xl transition-all duration-300 overflow-hidden ${
                                  isOpen 
                                    ? 'border-turquoise-500 shadow-lg shadow-turquoise-500/20' 
                                    : 'border-gray-700 hover:border-gray-600'
                                }`}
                              >
                                <button
                                  onClick={() => toggleFaq(categoryKey, index)}
                                  className="w-full p-4 text-left hover:bg-gray-800/70 transition-colors"
                                >
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-turquoise-500 to-cyan-500 flex items-center justify-center flex-shrink-0">
                                      <User size={16} className="text-white" />
                                    </div>
                                    <div className="flex-1">
                                      <p className="font-semibold text-white leading-relaxed">{faq.question}</p>
                                    </div>
                                    <div className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
                                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-turquoise-400">
                                        <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                      </svg>
                                    </div>
                                  </div>
                                </button>

                                {isOpen && (
                                  <div className="px-4 pb-4 animate-fadeIn">
                                    <div className="flex items-start gap-3 mb-3">
                                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                                        <Bot size={16} className="text-white" />
                                      </div>
                                      <div className="flex-1 pt-1">
                                        {showTyping ? (
                                          <div className="flex gap-1">
                                            <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                            <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                            <div className="w-2 h-2 bg-turquoise-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                                          </div>
                                        ) : (
                                          <div ref={answerRef}>
                                            <p className="text-gray-300 text-sm leading-relaxed mb-3">{faq.answer}</p>
                                            
                                            <div className="flex flex-wrap gap-2 mb-3">
                                              <button 
                                                onClick={() => window.location.href = '/book-consultation'}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-turquoise-500/10 hover:bg-turquoise-500/20 border border-turquoise-500/30 rounded-lg text-xs text-turquoise-400 transition-colors"
                                              >
                                                <Calendar size={12} />
                                                Book Consultation
                                              </button>
                                              <button 
                                                onClick={() => window.location.href = '/pricing'}
                                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-gray-700/50 hover:bg-gray-700 border border-gray-600 rounded-lg text-xs text-gray-300 transition-colors"
                                              >
                                                <ExternalLink size={12} />
                                                View Pricing
                                              </button>
                                            </div>

                                            <div className="flex items-center gap-3 pt-3 border-t border-gray-700">
                                              <span className="text-xs text-gray-400">Helpful?</span>
                                              <div className="flex gap-2">
                                                <button
                                                  onClick={() => handleFeedback(faqKey, true)}
                                                  className={`p-1.5 rounded-lg transition-all ${
                                                    feedback[faqKey] === true
                                                      ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                                                      : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 border border-gray-600'
                                                  }`}
                                                >
                                                  <ThumbsUp size={14} />
                                                </button>
                                                <button
                                                  onClick={() => handleFeedback(faqKey, false)}
                                                  className={`p-1.5 rounded-lg transition-all ${
                                                    feedback[faqKey] === false
                                                      ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                                                      : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700 border border-gray-600'
                                                  }`}
                                                >
                                                  <ThumbsDown size={14} />
                                                </button>
                                              </div>
                                              {hasFeedback && (
                                                <span className="text-xs text-turquoise-400">Thanks!</span>
                                              )}
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        </div>
        {/* End of hidden legacy FAQ code */}

        {/* Final CTA - Creative Layout with Image */}
        <div className="mt-12 bg-gradient-to-r from-turquoise-500/10 to-cyan-500/10 border border-turquoise-500/20 rounded-2xl overflow-hidden">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 p-8">
            {/* Left side - Content */}
            <div className="lg:col-span-7 flex flex-col justify-center text-center lg:text-left">
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">Still Have Questions?</h3>
              <p className="text-gray-400 mb-6 text-lg">Our experts are ready to provide personalized answers</p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <button 
                  className="inline-flex items-center justify-center gap-2 text-black font-semibold px-6 py-3 rounded-xl transition-all"
                  style={{ 
                    backgroundColor: '#40E0D0',
                    boxShadow: '0 10px 25px rgba(64, 224, 208, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#36C7B8';
                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(64, 224, 208, 0.5)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = '#40E0D0';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(64, 224, 208, 0.3)';
                  }}
                  onClick={() => window.location.href = '/book-consultation'}
                >
                  <Calendar size={20} />
                  Schedule Free Consultation
                </button>
                
                <button 
                  className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-white font-semibold px-6 py-3 rounded-xl border border-gray-700 transition-all"
                  onClick={() => {
                    const message = `Hi! I have some questions about your consulting services. Can we discuss?`;
                    window.open(`https://wa.me/919962536379?text=${encodeURIComponent(message)}`, '_blank');
                  }}
                >
                  <Send size={20} />
                  Ask via WhatsApp
                </button>
              </div>

              <div className="flex items-center justify-center lg:justify-start gap-2 mt-6 text-sm text-gray-400">
                <CheckCircle size={16} className="text-turquoise-400" />
                <span>Guaranteed response within 24 hours</span>
              </div>
            </div>

            {/* Right side - Support Image with Glow */}
            <div className="lg:col-span-5 flex items-center justify-center lg:justify-end">
              <div className="relative group w-full max-w-md">
                {/* Multi-color Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-turquoise-500/50 via-cyan-500/40 to-blue-500/30 blur-3xl opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
                
                {/* Image */}
                <img 
                  src="https://customer-assets.emergentagent.com/job_aiconsult-frontend/artifacts/aoj8c8kc_Still%20Have%20Questions.png"
                  alt="Support Representative"
                  className="relative w-full h-auto object-contain transform group-hover:scale-105 transition-transform duration-500"
                  style={{ filter: 'drop-shadow(0 0 30px rgba(20, 184, 166, 0.5))' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-turquoise-500/5 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-purple-500/5 rounded-full blur-3xl pointer-events-none"></div>
    </section>
  );
};

export default FAQSection;